import { SeriesOptionsType } from "highcharts";
import { ChartAxisProps, ChartDataSeries, ChartType } from "../../../components/Chart";
import { ReportingPeriods } from "../../../types";
import { getMonthNameAbbreviated } from "../../../utils/monthNames";
import ApiClient from "../../api";

export type CovenantsInterestCoverChartData = {
  series: Array<ChartDataSeries>;
  xAxis: ChartAxisProps;
  yAxis: ChartAxisProps;
};

export type CovenantsInterestCoverDetail = {
  financialYear: number;
  quarter: number;
  month: number;
  year: number;
  value: number;
};

export type CovenantsInterestCoverDBData = {
  proForma: {
    dataSeries: Array<CovenantsInterestCoverDetail>;
  };
  complianceCertificate: {
    dataSeries: Array<CovenantsInterestCoverDetail>;
  };
  covenantTest: {
    dataSeries: Array<CovenantsInterestCoverDetail>;
  };
};

export const getCovenantsInterestCoverChartData = async (
  companyId: number,
  reportingCycle: ReportingPeriods,
  financialYear: number,
  enableConversions: boolean = false,
  unit: string = ""
) => {
  const queryString = `?type=${
    ChartType.COVENANTS_INTEREST_COVER
  }&companyId=${companyId}&reportingCycle=${reportingCycle}&financialYear=${financialYear}&enableConversions=${
    enableConversions ? "true" : "false"
  }&unit=${unit ? unit : ""}`;

  const seriesFromDB = await ApiClient.get(`/charts${queryString}`);
  if (!seriesFromDB.data) return null;

  let xAxis: ChartAxisProps | null = null;
  const xAxisTitles = await getXAxisTitles(seriesFromDB.data, reportingCycle);

  if (xAxisTitles) {
    xAxis = {
      titles: xAxisTitles,
      colors: [],
    };
  }

  const yAxis: ChartAxisProps = {
    titles: [],
    colors: [],
  };

  const series = await getSeries(seriesFromDB.data, reportingCycle);

  return {
    series,
    xAxis,
    yAxis,
  } as CovenantsInterestCoverChartData;
};

const getXAxisTitles = async (data: CovenantsInterestCoverDBData, reportingCycle: ReportingPeriods) => {
  let detailToUse: CovenantsInterestCoverDetail[];
  let dataToReturn = [];

  // using the first arrays with data
  if (data.complianceCertificate.dataSeries?.length > 0) {
    detailToUse = data.complianceCertificate.dataSeries;
  } else if (data.covenantTest.dataSeries?.length > 0) {
    detailToUse = data.covenantTest.dataSeries;
  } else if (data.proForma.dataSeries?.length > 0) {
    detailToUse = data.proForma.dataSeries;
  } else {
    return [];
  }

  detailToUse = detailToUse.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));

  if (reportingCycle === ReportingPeriods.Quarterly) {
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else if (reportingCycle === ReportingPeriods.Monthly) {
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else {
    return [];
  }
};

// Use getSeries to decorate the series with specific elements to the chart.
const getSeries = async (data: CovenantsInterestCoverDBData, reportingCycle: ReportingPeriods) => {
  const series: SeriesOptionsType[] = [];
  series.push({
    animation: false,
    name: "Pro Forma",
    data: await getRawDataFromSeries(data.proForma.dataSeries, reportingCycle),
    color: "#A87EC9",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "As Per Compliance Certificate",
    data: await getRawDataFromSeries(data.complianceCertificate.dataSeries, reportingCycle),
    color: "#6a4e7f",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "Covenant Test",
    data: await getRawDataFromSeries(data.covenantTest.dataSeries, reportingCycle),
    color: "#D7913F",
    type: "column",
    borderRadius: 0,
  });
  return series;
};

const getRawDataFromSeries = async (data: CovenantsInterestCoverDetail[], reportingCycle: ReportingPeriods) => {
  if (!data || data.length === 0) return [];

  let rawDataToReturn = [];

  switch (reportingCycle) {
    case ReportingPeriods.Quarterly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    case ReportingPeriods.Monthly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    default:
      return [];
  }
};
